import {
    Badge,
    Button,
    Checkbox,
    Group,
    Loader,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AttractionFameEnum } from '@routellers/types/dist/city-attraction/enum/attraction-fame.enum';
import { CityPersonRO } from '@routellers/types/dist/city-person/city-person.ro';
import { CityPersonLocaleRO } from '@routellers/types/dist/city-person/city-person-locale.ro';
import { PersonDomainEnum } from '@routellers/types/dist/city-person/enum/person-domain.enum';
import { PersonReviewStatusEnum } from '@routellers/types/dist/city-person/enum/person-review-status.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { PersonEditor } from '../editors/person-editor';
import { enumLabel } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';
import { coverFromPhotos, LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';

const ALL = 'all';

const reviewColor: Record<PersonReviewStatusEnum, string> = {
    [PersonReviewStatusEnum.APPROVED]: 'green',
    [PersonReviewStatusEnum.NEED_REVIEW]: 'orange',
    [PersonReviewStatusEnum.REJECTED]: 'red',
    [PersonReviewStatusEnum.REJECTED_MCP]: 'red',
};

const STATUS_FILTER_ORDER = [
    PersonReviewStatusEnum.NEED_REVIEW,
    PersonReviewStatusEnum.APPROVED,
    PersonReviewStatusEnum.REJECTED,
    PersonReviewStatusEnum.REJECTED_MCP,
];

const FAME_FILTER_ORDER = [
    AttractionFameEnum.ICON,
    AttractionFameEnum.WELL_KNOWN,
    AttractionFameEnum.OFFBEAT,
    AttractionFameEnum.HIDDEN_GEM,
];

/** Full package: description + facts (same bar as fill-complete pipeline). */
const isPersonLocaleReady = (locale: CityPersonLocaleRO) =>
    !!locale.description?.trim() && !!locale.facts?.length;

const readyPersonLanguages = (row: CityPersonRO): Set<Languages> =>
    new Set((row.locales || []).filter(isPersonLocaleReady).map((locale) => locale.language));

const reviewStatusFromSearch = (value: string | null): string => {
    if (value && Object.values(PersonReviewStatusEnum).includes(value as PersonReviewStatusEnum)) {
        return value;
    }
    return ALL;
};

export const PeoplePage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [rows, setRows] = useState<CityPersonRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [reviewStatus, setReviewStatus] = useState<string>(() =>
        reviewStatusFromSearch(searchParams.get('reviewStatus')),
    );
    const [cityFilter, setCityFilter] = useState('all');
    const [domainFilter, setDomainFilter] = useState(ALL);
    const [fameFilter, setFameFilter] = useState(ALL);
    const [withoutPhoto, setWithoutPhoto] = useState(false);
    const [editorId, setEditorId] = useState<number | null>(null);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        const cityId = parseCityFilter(cityFilter);

        try {
            const where: Record<string, unknown> = {};
            if (cityId) where.cityId = cityId;
            if (reviewStatus !== ALL) where.reviewStatus = reviewStatus;
            if (domainFilter !== ALL) where.domain = domainFilter;
            if (fameFilter !== ALL) where.fame = fameFilter;
            if (withoutPhoto) where.hasPhoto = false;
            if (!Object.keys(where).length) where.id = null;

            const { data } = await api.cityPeopleApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: search.trim() || undefined,
                where,
            });

            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [cityFilter, reviewStatus, domainFilter, fameFilter, withoutPhoto]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFilter, reviewStatus, domainFilter, fameFilter, withoutPhoto, page]);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.people.title')}
                action={
                    <Button
                        onClick={() => {
                            setEditorId(null);
                            setCreating(true);
                        }}
                    >
                        {t('pages.people.create')}
                    </Button>
                }
            />

            <Group gap='sm' wrap='wrap'>
                <TextInput
                    placeholder={t('pages.people.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setPage(1);
                            load();
                        }
                    }}
                    w={240}
                />
                <CityQuickSelect
                    value={cityFilter}
                    onChange={(v) => {
                        setPage(1);
                        setCityFilter(v);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.people.statusAll') },
                        ...STATUS_FILTER_ORDER.map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.personReviewStatus', value),
                        })),
                    ]}
                    value={reviewStatus}
                    onChange={(v) => {
                        setPage(1);
                        setReviewStatus(v || ALL);
                    }}
                />
                <Select
                    w={200}
                    data={[
                        { value: ALL, label: t('pages.people.domainAll') },
                        ...Object.values(PersonDomainEnum).map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.personDomain', value),
                        })),
                    ]}
                    value={domainFilter}
                    onChange={(v) => {
                        setPage(1);
                        setDomainFilter(v || ALL);
                    }}
                />
                <Select
                    w={200}
                    data={[
                        { value: ALL, label: t('pages.people.fameAll') },
                        ...FAME_FILTER_ORDER.map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.attractionFame', value),
                        })),
                    ]}
                    value={fameFilter}
                    onChange={(v) => {
                        setPage(1);
                        setFameFilter(v || ALL);
                    }}
                />
                <Checkbox
                    label={t('pages.people.withoutPhoto')}
                    checked={withoutPhoto}
                    onChange={(e) => {
                        setPage(1);
                        setWithoutPhoto(e.currentTarget.checked);
                    }}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.people.columns.name')}</Table.Th>
                                    <Table.Th>{t('pages.people.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.people.columns.locales')}</Table.Th>
                                    <Table.Th>{t('pages.people.columns.domains')}</Table.Th>
                                    <Table.Th>{t('pages.people.columns.fame')}</Table.Th>
                                    <Table.Th>{t('pages.people.columns.review')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => {
                                    const ready = readyPersonLanguages(row);
                                    const expected = contentLanguagesForCountry(row.country);

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setEditorId(row.id)}
                                        >
                                            <Table.Td>
                                                <EntityThumb
                                                    src={coverFromPhotos(
                                                        row.photos,
                                                        row.coverPhotoIndex,
                                                    )}
                                                    name={row.displayName || row.name}
                                                    subtitle={row.slug}
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                {row.primaryCityTitle ||
                                                    (row.primaryCityId
                                                        ? `#${row.primaryCityId}`
                                                        : '—')}
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={4} wrap='wrap'>
                                                    {expected.map((language) => {
                                                        const filled = ready.has(language);

                                                        return (
                                                            <Badge
                                                                key={language}
                                                                size='sm'
                                                                variant={
                                                                    filled ? 'filled' : 'light'
                                                                }
                                                                color={filled ? 'blue' : 'gray'}
                                                            >
                                                                {language.toUpperCase()}
                                                            </Badge>
                                                        );
                                                    })}
                                                    {!expected.length && (
                                                        <Text size='xs' c='dimmed'>
                                                            —
                                                        </Text>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                {(row.domains || [])
                                                    .map((domain) =>
                                                        enumLabel(t, 'enums.personDomain', domain),
                                                    )
                                                    .join(', ') || '—'}
                                            </Table.Td>
                                            <Table.Td>
                                                {row.fame
                                                    ? enumLabel(t, 'enums.attractionFame', row.fame)
                                                    : '—'}
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={reviewColor[row.reviewStatus] || 'gray'}
                                                    variant='light'
                                                >
                                                    {enumLabel(
                                                        t,
                                                        'enums.personReviewStatus',
                                                        row.reviewStatus,
                                                    )}
                                                </Badge>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={6}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.people.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <PersonEditor
                opened={creating || editorId != null}
                personId={creating ? null : editorId}
                onClose={() => {
                    setCreating(false);
                    setEditorId(null);
                }}
                onSaved={load}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorId(id);
                }}
            />
        </Stack>
    );
};
